'use client';

import Link from "next/link";
import Image from "next/image";
import { HeaderConsultButton, CONSULT_BTN, CONSULT_HREF } from '@/components/leads/ConsultCta';
import { trackConsultCta } from '@/lib/analytics/leadTracking';
import { ShoppingCart, User, Menu, Search, LogOut, ChevronDown, ChevronRight, Settings, Package, List, Heart, Phone, Truck, ShieldCheck, Home, HelpCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useRouter, usePathname } from "next/navigation";
import WishlistDropdown from "@/components/ecommerce/WishlistDropdown";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { trackSearch } from "@/lib/analytics/ga4";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { MENU_SECTIONS, HELP_LINKS } from "@/lib/navigation/siteMenu";

interface AutocompleteResult {
  product_id: number;
  name: string;
  slug: string;
  base_price: number;
  starting_price?: number | null;
  category_name: string;
  primary_image: string | null;
}

// Type for user data
interface UserData {
  userId?: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}


// A pointer crossing the menu bar on its way down the page should not flash a
// panel open, so the first menu waits this long; moving between menus while one
// is open switches at once.
const MENU_OPEN_DELAY_MS = 150;

const MENU_BADGE_CLASS = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-yellow text-gray-900';


const Navbar = () => {
  const { itemCount } = useCart();
  const siteConfig = useSiteConfig();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navPath = usePathname() || '';
  const hideConsultCta = /^\/(cart|checkout)(\/|$)/.test(navPath);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Desktop search is now always visible, so the open/closed value is no longer
  // read — the setter is kept because mobile and the autocomplete links still
  // reset it when a result is chosen.
  const [, setSearchOpen] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<AutocompleteResult[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Key of the open desktop menu. As on the reference store, each menu-bar label is
  // a link to its page and its panel opens on hover. Keyboard users open it by
  // focusing the label; the panel follows the label in the DOM, so Tab walks
  // into it. Touch users tap through to the page.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuBarRef = useRef<HTMLDivElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuPanelRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Use site config for company info
  const companyInfo = {
    companyName: siteConfig.companyName,
    emergencyHotline: siteConfig.phone,
    tagline: siteConfig.tagline,
  };

  // Handle search submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackSearch(searchQuery.trim());
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setShowAutocomplete(false);
      setSearchOpen(false);
      setMobileMenuOpen(false);
    }
  };

  // Debounced autocomplete fetch
  const fetchAutocomplete = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.trim().length < 2) {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v2/search/autocomplete?q=${encodeURIComponent(query.trim())}`);
        if (res.ok) {
          const data = await res.json();
          const suggestions = data.data?.suggestions || [];
          setAutocompleteResults(suggestions);
          setShowAutocomplete(suggestions.length > 0);
        }
      } catch {
        setShowAutocomplete(false);
      }
    }, 300);
  }, []);

  // Close autocomplete on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close the open desktop menu on an outside click or Escape.
  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu]);

  // Company info is now provided via SiteConfigContext - no need to fetch

  // Fetch user data
  const fetchUser = async () => {
    try {
      const response = await fetch('/api/v2/auth/me');
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        setUser(data.user);
      } else {
        // Don't log 401 errors as they're expected for unauthenticated users
        if (response.status !== 401) {
          console.error('Error fetching user:', response.status, response.statusText);
        }
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    fetchUser();
  }, []);

  // Listen for logout events from other parts of the app
  useEffect(() => {
    const handleAuthChange = () => {
      fetchUser();
    };

    // Listen for custom logout events
    window.addEventListener('userLoggedOut', handleAuthChange);
    
    // Listen for storage changes (in case auth token is cleared)
    window.addEventListener('storage', (e) => {
      if (e.key === 'auth_logout') {
        fetchUser();
      }
    });

    // Also check auth status when the window gains focus
    window.addEventListener('focus', fetchUser);

    return () => {
      window.removeEventListener('userLoggedOut', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('focus', fetchUser);
    };
  }, []);

  // Hover handlers for the desktop menu bar.
  const hoverMenu = (key: string) => {
    if (menuOpenTimer.current) clearTimeout(menuOpenTimer.current);
    if (openMenu) {
      setOpenMenu(key);
    } else {
      menuOpenTimer.current = setTimeout(() => setOpenMenu(key), MENU_OPEN_DELAY_MS);
    }
  };
  const leaveMenu = () => {
    if (menuOpenTimer.current) clearTimeout(menuOpenTimer.current);
    setOpenMenu(null);
  };
  useEffect(() => () => { if (menuOpenTimer.current) clearTimeout(menuOpenTimer.current); }, []);

  // Handle click outside to close the account and help dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setHelpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideButton = mobileMenuRef.current && mobileMenuRef.current.contains(target);
      const isInsidePanel = mobileMenuPanelRef.current && mobileMenuPanelRef.current.contains(target);
      if (!isInsideButton && !isInsidePanel) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Handle logout
  const handleLogout = async () => {
    try {
      const response = await fetch('/api/v2/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Clear user state and redirect to home
        setUser(null);
        router.push('/');
        router.refresh(); // Force a refresh to update authentication state
      }
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Get dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!user) return '/login';

    switch (user.role) {
      case 'admin':
        return '/admin';
      case 'vendor':
        return '/vendor';
      case 'dealer':
        return '/dealer';
      case 'installer':
        return '/installer';
      default:
        return '/account';
    }
  };

  // Get display name
  const getDisplayName = () => {
    if (!user) return '';
    return user.firstName || user.email.split('@')[0];
  };

  const handleSignIn = () => {
    // Include current URL as redirect parameter for customers
    const currentPath = window.location.pathname + window.location.search;
    // Only include redirect for product pages or cart/checkout
    if (currentPath.includes('/products/configure') || 
        currentPath.includes('/cart') || 
        currentPath.includes('/checkout')) {
      router.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else {
      router.push('/login');
    }
  };

  return (
    <>
      {/* Trust bar — the same four promises on every page, the way
          the reference store runs one under its logo. Not part of the sticky
          header, so it scrolls away and does not cost vertical space. Narrow
          screens keep the first two promises; the phone moves into the header. */}
      <div className="bg-gray-50 border-b border-slate-200 text-xs text-gray-700">
        <div className="container mx-auto px-4 flex items-center justify-center gap-x-6 gap-y-1 py-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Truck size={14} aria-hidden="true" className="text-heading-charcoal" />
            Free Shipping on All Orders
          </span>
          <Link prefetch={false} href="/perfect-fit-guarantee" className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-primary-red">
            <ShieldCheck size={14} aria-hidden="true" className="text-heading-charcoal" />
            Perfect Fit Guarantee
          </Link>
          <Link prefetch={false} href="/consultation" className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap hover:text-primary-red">
            <Home size={14} aria-hidden="true" className="text-heading-charcoal" />
            Free In-Home Consultation
          </Link>
          <a
            href={`tel:${companyInfo.emergencyHotline.replace(/[^0-9]/g, '')}`}
            data-track="header-topbar"
            className="hidden md:inline-flex items-center gap-1.5 whitespace-nowrap font-semibold hover:text-primary-red"
          >
            <Phone size={14} aria-hidden="true" className="text-heading-charcoal" />
            Call/Text {companyInfo.emergencyHotline}
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <header className="bg-brand-header border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      {/* Row 1: logo, search, account. The menus live in their own row below,
          so search no longer competes with them for width — it was hidden
          behind an icon below 1536px for exactly that reason. */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between gap-3 h-14 md:h-16">
          {/* Mobile menu button */}
          <div className="flex lg:hidden" ref={mobileMenuRef}>
            <button
              type="button"
              className="text-brand-navtext hover:text-primary-red inline-flex items-center justify-center min-w-11 min-h-11 rounded-lg hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu size={24} />
            </button>
          </div>

          {/* Logo: transparent charcoal emblem + Libre Baskerville wordmark.
              Wordmark is dynamic per tenant from siteConfig (e.g., "Smart Blinds™ Pro"). */}
          <div className="min-w-0 flex-1 lg:flex-none flex items-center">
            <Link
              prefetch={false}
              href="/"
              aria-label={siteConfig.companyName || 'Smart Blinds'}
              className="min-w-0 flex items-center gap-2 md:gap-2.5 group"
            >
              <Image
                src="/images/logo/logo-charcoal-v3.png"
                alt=""
                aria-hidden="true"
                width={44}
                height={44}
                priority
                className="w-8 h-8 md:w-11 md:h-11 shrink-0 object-contain"
              />
              <span className="font-display font-bold text-brand-navtext text-sm md:text-lg lg:text-xl tracking-tight truncate leading-none">
                {siteConfig.companyName || 'Smart Blinds'}
              </span>
            </Link>
          </div>

          {/* Search — always visible from md up. Site search is the
              highest-intent path on a catalogue this size; every major
              competitor keeps a persistent input in the header. */}
          <div className="hidden md:block relative flex-1 max-w-xl mx-2 lg:mx-6" ref={autocompleteRef}>
            <form onSubmit={handleSearch} className="relative">
              <input maxLength={80}
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  fetchAutocomplete(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchOpen(false);
                    setShowAutocomplete(false);
                  }
                }}
                aria-label="Search products"
                placeholder="What can we help you find?"
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-primary-red"
              />
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </form>
            {showAutocomplete && autocompleteResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                {autocompleteResults.map((item) => (
                  <Link
                    key={item.product_id}
                    href={`/products/configure/${item.slug}`}
                    onClick={() => { setSearchOpen(false); setShowAutocomplete(false); setSearchQuery(''); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    {item.primary_image ? (
                      <Image src={item.primary_image} alt="" width={36} height={36} className="rounded object-cover w-9 h-9" />
                    ) : (
                      <div className="w-9 h-9 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">IMG</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.category_name}</p>
                    </div>
                    <span className="text-sm font-semibold text-sb-gold whitespace-nowrap">
                      {item.starting_price != null ? `From $${Number(item.starting_price).toFixed(2)}` : 'Request quote'}
                    </span>
                  </Link>
                ))}
                <Link
                  href={`/search?q=${encodeURIComponent(searchQuery.trim())}`}
                  onClick={() => { trackSearch(searchQuery.trim()); setSearchOpen(false); setShowAutocomplete(false); setSearchQuery(''); }}
                  className="block px-4 py-2.5 text-center text-sm font-medium text-sb-gold hover:bg-gray-50 border-t border-gray-100"
                >
                  View all results for &quot;{searchQuery.trim()}&quot;
                </Link>
              </div>
            )}
          </div>

          {/* Right side icons */}
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            {/* Free Consultation (md+; phones get the green strip, sticky bar and
                drawer button — the 390px header has no room). Not on cart or
                checkout, where nothing should pull a buyer away. */}
            {!hideConsultCta && <HeaderConsultButton className="hidden md:inline-flex mr-1" />}
            {/* Language Switcher */}
            <div className="hidden sm:block"><LanguageSwitcher /></div>

            {/* Search icon below md, where the input does not fit; links to the
                search page rather than toggling an overlay. */}
            <Link
              href="/search"
              aria-label="Search products"
              className="hidden sm:inline-flex md:hidden text-brand-navtext hover:text-primary-red items-center justify-center min-w-11 min-h-11 rounded-full hover:bg-slate-100 transition-colors"
            >
              <Search size={20} />
            </Link>
            {/* Phone icon below md (phones and small tablets): from md up the
                number is in the trust bar. Phones used to get it only inside the
                drawer, so product, cart and checkout pages had no visible number
                on mobile, where most calls come from. */}
            <a
              href={`tel:${companyInfo.emergencyHotline.replace(/[^0-9]/g, '')}`}
            data-track="header-icon"
              className="flex md:hidden text-brand-navtext hover:text-primary-red min-w-11 min-h-11 items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
              aria-label="Call or text us"
            >
              <Phone size={20} />
            </a>

            {/* Wishlist Dropdown — hidden on mobile to keep header to 4 critical icons */}
            <div className="hidden sm:block">
              <WishlistDropdown />
            </div>

            {/* Help — a "?" dropdown, as on the reference store. Desktop only; the
                mobile drawer lists the same links. */}
            <div className="relative hidden lg:block" ref={helpRef}>
              <button
                type="button"
                onClick={() => setHelpOpen(!helpOpen)}
                aria-label="Help"
                aria-expanded={helpOpen}
                aria-haspopup="true"
                className={`inline-flex items-center justify-center min-w-11 min-h-11 rounded-full transition-colors ${helpOpen ? 'bg-slate-100 text-primary-red' : 'text-brand-navtext hover:text-primary-red hover:bg-slate-100'}`}
              >
                <HelpCircle size={20} />
              </button>
              {helpOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-72 bg-white rounded-lg shadow-xl border border-gray-100 p-2 z-50"
                  onClick={() => setHelpOpen(false)}
                >
                  <p className="px-3 pt-1.5 pb-1 text-xs font-semibold text-gray-500">Help</p>
                  <ul>
                    {HELP_LINKS.map((link) => (
                      <li key={link.href}>
                        <Link
                          prefetch={false}
                          href={link.href}
                          className="block px-3 py-2 rounded-md text-sm font-medium text-gray-800 hover:bg-sb-gold-tint hover:text-heading-charcoal transition-colors"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={`tel:${companyInfo.emergencyHotline.replace(/[^0-9]/g, '')}`}
            data-track="header-help-menu"
                    className="flex items-center gap-2 mt-1 px-3 py-2.5 border-t border-gray-100 text-sm font-semibold text-gray-900 hover:text-primary-red"
                  >
                    <Phone size={16} aria-hidden="true" />
                    Call/Text {companyInfo.emergencyHotline}
                  </a>
                </div>
              )}
            </div>

            {/* User Account - changes based on auth state */}
            {!loading && (
              user ? (
                <div className="hidden sm:block relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="hidden sm:flex items-center text-brand-navtext hover:text-primary-red min-w-11 min-h-11 px-2 rounded-full hover:bg-slate-100 transition-colors"
                  >
                    <User size={20} />
                    <span className="hidden sm:inline-block ml-1 text-sm font-medium">{getDisplayName()}</span>
                    <ChevronDown size={14} className="ml-0.5 opacity-70" />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-20 border border-gray-100">
                      <div className="py-3 px-4 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                        <p className="text-sm font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                        <span className="text-[10px] mt-1.5 /10 text-primary-red px-2 py-0.5 rounded-full inline-block font-medium capitalize">{user.role}</span>
                      </div>
                      <div className="py-1.5">
                        <Link
                          href={getDashboardUrl()}
                          onClick={() => setDropdownOpen(false)}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-sb-gold-tint hover:text-heading-charcoal font-medium flex items-center transition-colors"
                        >
                          <Settings size={16} className="mr-2.5 opacity-60" />
                          Dashboard
                        </Link>
                        <Link
                          href="/account/orders"
                          onClick={() => setDropdownOpen(false)}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-sb-gold-tint hover:text-heading-charcoal font-medium flex items-center transition-colors"
                        >
                          <Package size={16} className="mr-2.5 opacity-60" />
                          My Orders
                        </Link>
                        <Link
                          href="/account/configurations"
                          onClick={() => setDropdownOpen(false)}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-sb-gold-tint hover:text-heading-charcoal font-medium flex items-center transition-colors"
                        >
                          <List size={16} className="mr-2.5 opacity-60" />
                          Saved Configurations
                        </Link>
                        <Link
                          href="/account/wishlist"
                          onClick={() => setDropdownOpen(false)}
                          className="px-4 py-2 text-sm text-gray-700 hover:bg-sb-gold-tint hover:text-heading-charcoal font-medium flex items-center transition-colors"
                        >
                          <Heart size={16} className="mr-2.5 opacity-60" />
                          Wishlist
                        </Link>
                        <div className="border-t border-gray-100 mt-1.5 pt-1.5">
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-sb-gold-tint hover:text-heading-charcoal font-medium flex items-center transition-colors"
                          >
                            <LogOut size={16} className="mr-2.5 opacity-60" />
                            Sign Out
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="hidden sm:flex items-center text-brand-navtext hover:text-primary-red min-w-11 min-h-11 px-2 rounded-full hover:bg-slate-100 transition-colors"
                  aria-label="Account"
                >
                  <User size={20} />
                  <span className="hidden sm:inline-block ml-1 text-sm font-bold whitespace-nowrap">Sign In</span>
                </button>
              )
            )}

            {/* Cart — visible to everyone, including staff. Hiding it from
                staff made the cart unreachable for them even once items were in
                it; one storefront for all roles. */}
            {true && (
              <Link
                href="/cart"
                className="text-brand-navtext hover:text-primary-red inline-flex items-center justify-center min-w-11 min-h-11 rounded-full hover:bg-slate-100 transition-colors relative"
                aria-label="Cart"
              >
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute top-1 right-1 bg-accent-yellow text-gray-900 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: the menu bar (desktop), laid out like the reference store: one
          label per product type, each opening a full-width panel. The row is
          the panels' positioning parent; the item wrappers are deliberately NOT
          positioned, so a panel spans the whole row while still sitting inside
          its item in the DOM (hover keeps it open, Tab walks into it). No
          `overflow-hidden` anywhere on this row: it once clipped every dropdown
          to the bar's height (2026-09-19). */}
      <div
        ref={menuBarRef}
        className="hidden lg:block relative border-t border-slate-200"
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpenMenu(null); }}
      >
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between" aria-label="Main navigation">
            {MENU_SECTIONS.map((section) => {
              const isOpen = openMenu === section.key;
              return (
                <div
                  key={section.key}
                  onMouseEnter={() => hoverMenu(section.key)}
                  onMouseLeave={leaveMenu}
                >
                  <Link
                    prefetch={false}
                    href={section.href}
                    onFocus={() => setOpenMenu(section.key)}
                    onClick={leaveMenu}
                    aria-expanded={isOpen}
                    aria-controls={`menu-panel-${section.key}`}
                    className={`block px-3 py-3 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${isOpen ? 'border-primary-red text-primary-red' : 'border-transparent text-brand-navtext hover:text-primary-red'}`}
                  >
                    {section.label}
                  </Link>
                  {isOpen && (
                    // Client-side navigation keeps the Navbar mounted, so a
                    // click inside must close the panel or it stays open on the
                    // next page.
                    <div
                      id={`menu-panel-${section.key}`}
                      className="absolute left-0 right-0 top-full z-50 bg-white border-t border-slate-200 shadow-xl"
                      onClick={leaveMenu}
                    >
                      <div className="container mx-auto px-4 py-8 flex gap-10 max-h-[calc(100vh-9rem)] overflow-y-auto overscroll-contain">
                        {section.image && (
                          <Image
                            src={section.image.src}
                            alt={section.image.alt}
                            width={256}
                            height={256}
                            sizes="256px"
                            className="w-48 h-48 xl:w-64 xl:h-64 shrink-0 rounded-lg object-cover bg-gray-100"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-3 gap-x-10">
                            {section.columns.map((column, i) => (
                              <div key={i} className="flex flex-col gap-6">
                                {column.map((group) => (
                                  <div key={group.title}>
                                    {group.href ? (
                                      <Link
                                        prefetch={false}
                                        href={group.href}
                                        className="block mb-2 text-xs font-semibold text-gray-500 hover:text-primary-red"
                                      >
                                        {group.title}
                                      </Link>
                                    ) : (
                                      <p className="mb-2 text-xs font-semibold text-gray-500">{group.title}</p>
                                    )}
                                    <ul className="space-y-1">
                                      {group.links.map((link) => (
                                        <li key={link.href + link.label}>
                                          <Link
                                            prefetch={false}
                                            href={link.href}
                                            className="inline-flex items-center gap-2 py-1 text-sm font-semibold text-gray-900 hover:underline underline-offset-4"
                                          >
                                            {link.label}
                                            {link.badge && <span className={MENU_BADGE_CLASS}>{link.badge}</span>}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                          <Link
                            prefetch={false}
                            href={section.shopAll.href}
                            className="mt-8 inline-flex items-center gap-1.5 px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 transition-colors"
                          >
                            {section.shopAll.label}
                            <ChevronRight size={16} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile Search Bar moved INTO the drawer (was always-visible, ate ~70px above the fold).
          See the search input rendered at the top of the mobile menu below. */}

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => { setMobileMenuOpen(false); setMobileExpandedSection(null); }}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div ref={mobileMenuPanelRef} className="lg:hidden bg-white border-t border-gray-200 relative z-50 max-h-[80vh] overflow-y-auto">
          <div className="sm:hidden px-3 py-2 flex items-center gap-3"><span className="text-sm">Language</span><LanguageSwitcher /></div>
          {/* Search at top of drawer (replaces the always-visible mobile search bar) */}
          <div className="px-3 pt-3 pb-2 border-b border-gray-100">
            <form onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }} className="relative">
              <input maxLength={100}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                aria-label="Search products"
                className="w-full pl-10 pr-4 h-12 bg-gray-50 border border-gray-200 rounded-full text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-red focus:bg-white"
              />
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            </form>
          </div>

          {/* Free consultation — same green accent as the header button */}
          <Link
            prefetch={false}
            href={CONSULT_HREF}
            data-track="consult-mobile-menu"
            className={`${CONSULT_BTN} flex items-center justify-center gap-2 mx-3 mt-3 h-12 rounded-full font-bold text-base`}
            onClick={() => { trackConsultCta('mobile_menu'); setMobileMenuOpen(false); }}
          >
            Free In-Home Consultation
          </Link>

          {/* Quick call CTA */}
          <a
            href={`tel:${companyInfo.emergencyHotline.replace(/[^0-9]/g, '')}`}
            data-track="mobile-menu-call"
            className="flex sm:hidden items-center justify-center gap-2 mx-3 mt-3 h-12 rounded-full bg-primary-red text-white font-semibold text-base hover:bg-primary-red/90"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Phone size={18} />
            Call {companyInfo.emergencyHotline}
          </a>

          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* The same menus as desktop, as accordions. */}
            {MENU_SECTIONS.map((section) => {
              const expanded = mobileExpandedSection === section.key;
              return (
                <Fragment key={section.key}>
                  <button
                    type="button"
                    onClick={() => setMobileExpandedSection(expanded ? null : section.key)}
                    aria-expanded={expanded}
                    className="w-full flex items-center justify-between px-3 py-3 text-base font-bold text-gray-800 hover:bg-gray-50 rounded-md"
                  >
                    {section.label}
                    {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {expanded && (
                    <div className="pl-4 pb-2 space-y-3">
                      {section.columns.flat().map((group) => (
                        <div key={group.title}>
                          <p className="px-3 pt-1 text-xs font-semibold text-gray-500">{group.title}</p>
                          {group.links.map((link) => (
                            <Link
                              key={link.href + link.label}
                              prefetch={false}
                              href={link.href}
                              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint font-medium"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {link.label}
                              {link.badge && <span className={MENU_BADGE_CLASS}>{link.badge}</span>}
                            </Link>
                          ))}
                        </div>
                      ))}
                      <Link
                        prefetch={false}
                        href={section.shopAll.href}
                        className="block px-3 py-2 rounded-md text-sm font-bold text-primary-red hover:bg-sb-gold-tint"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {section.shopAll.label} →
                      </Link>
                    </div>
                  )}
                </Fragment>
              );
            })}

            {/* Help — the desktop "?" dropdown's links, as the last section. */}
            <button
              type="button"
              onClick={() => setMobileExpandedSection(mobileExpandedSection === 'help' ? null : 'help')}
              aria-expanded={mobileExpandedSection === 'help'}
              className="w-full flex items-center justify-between px-3 py-3 text-base font-bold text-gray-800 hover:bg-gray-50 rounded-md"
            >
              Help
              {mobileExpandedSection === 'help' ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            {mobileExpandedSection === 'help' && (
              <div className="pl-4 pb-2">
                {HELP_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    prefetch={false}
                    href={link.href}
                    className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Free Consultation CTA */}
            <Link
              href="/consultation"
              className="block mx-3 mt-2 text-center bg-sb-gold hover:bg-sb-gold-hover text-black font-bold px-4 py-2.5 rounded-full text-sm transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Free Consultation
            </Link>

            {/* Divider */}
            <div className="border-t border-gray-200 my-2"></div>

            {user ? (
              <>
                <Link prefetch={false} href={getDashboardUrl()} className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                <Link prefetch={false} href="/account/orders" className="block px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint" onClick={() => setMobileMenuOpen(false)}>My Orders</Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => { handleSignIn(); setMobileMenuOpen(false); }}
                className="block w-full text-left px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-heading-charcoal hover:bg-sb-gold-tint"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      )}
    </header>
    </>
  );
};

export default Navbar;
